import { useState, useEffect } from 'react'
import { Bell, CheckCircle, AlertTriangle, Users, Clock } from 'lucide-react'

interface Notification {
  notification_id: string
  kind: string
  subject: string
  body?: string
  link_url?: string
  read_at?: number
  created_at: number
}

function kindIcon(kind: string) {
  if (kind.includes('complete')) return <CheckCircle size={16} strokeWidth={1.75} className="text-sage-medium" />
  if (kind.includes('fail')) return <AlertTriangle size={16} strokeWidth={1.75} className="text-crimson-earth" />
  if (kind.includes('invite')) return <Users size={16} strokeWidth={1.75} className="text-echo-indigo" />
  return <Clock size={16} strokeWidth={1.75} className="text-earth-amber" />
}

function timeAgo(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000)
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

export function InboxPage() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  const token = localStorage.getItem('echo_session')
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await fetch('/api/v1/notifications', { headers })
        if (res.ok) {
          setNotifications(await res.json() as Notification[])
        }
      } catch {
        setNotifications([])
      } finally {
        setLoading(false)
      }
    }
    fetchNotifs()
  }, [])

  const markRead = async (id: string) => {
    await fetch(`/api/v1/notifications/${id}/read`, { method: 'POST', headers })
    setNotifications(prev =>
      prev.map(n => n.notification_id === id ? { ...n, read_at: Date.now() } : n)
    )
  }

  const unread = notifications.filter(n => !n.read_at).length

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground animate-pulse">Loading notifications...</div>
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Bell size={20} strokeWidth={1.75} className="text-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Inbox</h2>
        {unread > 0 && (
          <span className="bg-echo-indigo text-white text-xs font-medium px-2 py-0.5 rounded-full">
            {unread}
          </span>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Bell className="mx-auto mb-3 opacity-30" size={40} strokeWidth={1.75} />
          <p className="text-sm">No notifications yet</p>
        </div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
          {notifications.map(n => (
            <div
              key={n.notification_id}
              className={`p-4 flex gap-3 transition-colors ${n.read_at ? 'bg-background' : 'bg-echo-indigo/5'}`}
            >
              <div className="mt-0.5">{kindIcon(n.kind)}</div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${n.read_at ? 'text-foreground' : 'font-medium text-foreground'}`}>
                  {n.subject}
                </p>
                {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                  {n.link_url && (
                    <a href={n.link_url} className="text-xs text-echo-indigo hover:underline">View →</a>
                  )}
                  {!n.read_at && (
                    <button
                      onClick={() => markRead(n.notification_id)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default InboxPage
